// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Card, Checkbox, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { classNames } from '@shared/ui-kit/utils';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  NavigationControls,
  ScreenHeader,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { useSignature } from './use-signature';

export const Signature = defineComponent({
  displayName: 'AccountOpeningSignature',
  useSetup: useSignature,
  View: ({ form, onNext, isFormValid, isUpdatingClientOnboardingProfile }) => {
    return (
      <ScreenLayout
        actions={
          <NavigationControls
            form="signature-form"
            isFormValid={isFormValid}
            loading={isUpdatingClientOnboardingProfile}
            nextLabel={
              <Translation
                i18nKey="account-opening.signature.cta"
                ns="components"
              />
            }
          />
        }
      >
        <div className="w-full">
          <form
            id="signature-form"
            onSubmit={form.handleSubmit(onNext)}
            aria-busy={isUpdatingClientOnboardingProfile || undefined}
          >
            <fieldset
              disabled={isUpdatingClientOnboardingProfile}
              className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0"
            >
              <ScreenHeader
                title={
                  <Translation
                    i18nKey="account-opening.screens.signature.title"
                    ns="components"
                  />
                }
                subtitle={
                  <Translation
                    i18nKey="account-opening.screens.signature.subtitle"
                    ns="components"
                  />
                }
              />

              <Card className="!rounded-xl !p-6" variant="outlined">
                <div className="flex flex-col gap-6">
                  <Typography
                    variant="h5"
                    className="!text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.signature.documents.title"
                      ns="components"
                    />
                  </Typography>

                  <div className="flex flex-col gap-4">
                    <a
                      href="/docs/contrato-marco.pdf"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 rounded-2xl border border-border-light bg-white px-4 py-3 transition-all hover:border-primary hover:bg-primary/5"
                      data-testid="signature-document-account-contract"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        <AppIcon icon="FileText" size="base" color="main" />
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1">
                        <Typography
                          variant="body1"
                          className="!truncate !text-base !font-semibold !text-title-navy"
                        >
                          <Translation
                            i18nKey="account-opening.signature.documents.account-contract.title"
                            ns="components"
                          />
                        </Typography>

                        <Typography
                          variant="body2"
                          className="!text-sm !font-normal !text-subtitle"
                        >
                          <Translation
                            i18nKey="account-opening.signature.documents.account-contract.description"
                            ns="components"
                          />
                        </Typography>
                      </div>

                      <div className="flex shrink-0 items-center justify-center text-subtitle">
                        <AppIcon icon="Download" size="base" color="inherit" />
                      </div>
                    </a>

                    <a
                      href="/docs/anexo-tarifas.pdf"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 rounded-2xl border border-border-light bg-white px-4 py-3 transition-all hover:border-primary hover:bg-primary/5"
                      data-testid="signature-document-fee-annex"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        <AppIcon icon="FileText" size="base" color="main" />
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1">
                        <Typography
                          variant="body1"
                          className="!truncate !text-base !font-semibold !text-title-navy"
                        >
                          <Translation
                            i18nKey="account-opening.signature.documents.fee-annex.title"
                            ns="components"
                          />
                        </Typography>

                        <Typography
                          variant="body2"
                          className="!text-sm !font-normal !text-subtitle"
                        >
                          <Translation
                            i18nKey="account-opening.signature.documents.fee-annex.description"
                            ns="components"
                          />
                        </Typography>
                      </div>

                      <div className="flex shrink-0 items-center justify-center text-subtitle">
                        <AppIcon icon="Download" size="base" color="inherit" />
                      </div>
                    </a>

                    <a
                      href="/docs/anexo-proteccion-datos.pdf"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 rounded-2xl border border-border-light bg-white px-4 py-3 transition-all hover:border-primary hover:bg-primary/5"
                      data-testid="signature-document-data-protection"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                        <AppIcon icon="FileText" size="base" color="main" />
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1">
                        <Typography
                          variant="body1"
                          className="!truncate !text-base !font-semibold !text-title-navy"
                        >
                          <Translation
                            i18nKey="account-opening.signature.documents.data-protection.title"
                            ns="components"
                          />
                        </Typography>

                        <Typography
                          variant="body2"
                          className="!text-sm !font-normal !text-subtitle"
                        >
                          <Translation
                            i18nKey="account-opening.signature.documents.data-protection.description"
                            ns="components"
                          />
                        </Typography>
                      </div>

                      <div className="flex shrink-0 items-center justify-center text-subtitle">
                        <AppIcon icon="Download" size="base" color="inherit" />
                      </div>
                    </a>
                  </div>
                </div>
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <div className="flex flex-col gap-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-page">
                      <AppIcon icon="Shield" size="base" color="gray" />
                    </div>

                    <Typography
                      variant="h5"
                      className="!text-h5 !font-semibold !text-title-navy"
                    >
                      <Translation
                        i18nKey="account-opening.signature.account-summary.title"
                        ns="components"
                      />
                    </Typography>
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="flex flex-col gap-1">
                      <Typography
                        variant="body2"
                        className="!text-sm !font-normal !text-subtitle"
                      >
                        <Translation
                          i18nKey="account-opening.signature.account-summary.advisor.label"
                          ns="components"
                        />
                      </Typography>

                      <Typography
                        variant="body1"
                        className="!text-base !font-medium !text-title-navy"
                      >
                        <Translation
                          i18nKey="glossary.silver-alpha-asset-management-sgiic"
                          ns="glossary"
                        />
                      </Typography>
                    </div>

                    <div className="flex flex-col gap-1">
                      <Typography
                        variant="body2"
                        className="!text-sm !font-normal !text-subtitle"
                      >
                        <Translation
                          i18nKey="account-opening.signature.account-summary.custodian.label"
                          ns="components"
                        />
                      </Typography>

                      <Typography
                        variant="body1"
                        className="!text-base !font-medium !text-title-navy"
                      >
                        <Translation
                          i18nKey="glossary.inversis-bank"
                          ns="glossary"
                        />
                      </Typography>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <div className="flex flex-col gap-6">
                  <Typography
                    variant="h5"
                    className="!text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.signature.confirmation.title"
                      ns="components"
                    />
                  </Typography>

                  <div className="flex flex-col gap-3">
                    <Controller
                      control={form.control}
                      name="acceptedAccountTerms"
                      render={({ field }) => (
                        <button
                          type="button"
                          disabled={isUpdatingClientOnboardingProfile}
                          onClick={() => field.onChange(!field.value)}
                          className={classNames(
                            'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-4 text-left transition-all hover:border-primary hover:bg-primary/5',
                            {
                              'border-primary bg-primary/5 ring-1 ring-primary/20':
                                field.value,
                            },
                          )}
                          data-testid="signature-confirmation-acceptedAccountTerms"
                        >
                          <Checkbox
                            disabled={isUpdatingClientOnboardingProfile}
                            checked={field.value}
                            className="!p-0 !text-primary hover:!bg-transparent"
                            onClick={event => event.stopPropagation()}
                            onChange={() => field.onChange(!field.value)}
                          />

                          <Typography
                            variant="body1"
                            className="!text-sm !font-normal !text-title-navy sm:!text-base"
                          >
                            <Translation
                              i18nKey="account-opening.signature.confirmation.accept-account-terms"
                              ns="components"
                            />
                          </Typography>
                        </button>
                      )}
                    />

                    <Controller
                      control={form.control}
                      name="acceptedDataProtection"
                      render={({ field }) => (
                        <button
                          type="button"
                          disabled={isUpdatingClientOnboardingProfile}
                          onClick={() => field.onChange(!field.value)}
                          className={classNames(
                            'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-4 text-left transition-all hover:border-primary hover:bg-primary/5',
                            {
                              'border-primary bg-primary/5 ring-1 ring-primary/20':
                                field.value,
                            },
                          )}
                          data-testid="signature-confirmation-acceptedDataProtection"
                        >
                          <Checkbox
                            disabled={isUpdatingClientOnboardingProfile}
                            checked={field.value}
                            className="!p-0 !text-primary hover:!bg-transparent"
                            onClick={event => event.stopPropagation()}
                            onChange={() => field.onChange(!field.value)}
                          />

                          <Typography
                            variant="body1"
                            className="!text-sm !font-normal !text-title-navy sm:!text-base"
                          >
                            <Translation
                              i18nKey="account-opening.signature.confirmation.accept-data-protection"
                              ns="components"
                            />
                          </Typography>
                        </button>
                      )}
                    />

                    <Controller
                      control={form.control}
                      name="confirmedInformationTruthfulness"
                      render={({ field }) => (
                        <button
                          type="button"
                          disabled={isUpdatingClientOnboardingProfile}
                          onClick={() => field.onChange(!field.value)}
                          className={classNames(
                            'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-4 text-left transition-all hover:border-primary hover:bg-primary/5',
                            {
                              'border-primary bg-primary/5 ring-1 ring-primary/20':
                                field.value,
                            },
                          )}
                          data-testid="signature-confirmation-confirmedInformationTruthfulness"
                        >
                          <Checkbox
                            disabled={isUpdatingClientOnboardingProfile}
                            checked={field.value}
                            className="!p-0 !text-primary hover:!bg-transparent"
                            onClick={event => event.stopPropagation()}
                            onChange={() => field.onChange(!field.value)}
                          />

                          <Typography
                            variant="body1"
                            className="!text-sm !font-normal !text-title-navy sm:!text-base"
                          >
                            <Translation
                              i18nKey="account-opening.signature.confirmation.confirm-information-truthfulness"
                              ns="components"
                            />
                          </Typography>
                        </button>
                      )}
                    />
                  </div>
                </div>
              </Card>
            </fieldset>
          </form>
        </div>
      </ScreenLayout>
    );
  },
});
